import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { PropTypes } from 'prop-types';
import Joi from 'joi';

import {
    useGetActionConditionsQuery,
    useGetQuestionsQuery,
    useUpdateTunnelActionMutation,
} from 'api/main';
import { editingActionIdSet, editingConditionIdSet } from 'reducers/customTunnel';
import { customTunnelSelectors } from 'reducers';
import { customTunnelSchemas } from 'schemas';
import { ucFirst, validateSchema } from 'utils/helpers';
import { useDialog } from 'utils/hooks';
import { alertType, conditionType } from 'utils/constants';
import { texts } from 'utils/languages/fr';

import ConditionItem from './ConditionItem';
import ScoringLimits from './ScoringLimits';
import {
    ActionButton,
    CancelButton,
    CheckboxLabel,
    ConfirmDialog,
    CustomFormLabel,
    CustomSelect,
    CustomTextField,
    ErrorMessage,
    Loader,
} from 'components';
import { Box, MenuItem } from '@mui/material';

const alertLabels = {
    [alertType.WARNING]: ucFirst(texts.ALERTS.ORANGE),
    [alertType.CRITICAL]: ucFirst(texts.ALERTS.RED),
    [alertType.OK]: ucFirst(texts.ALERTS.GREEN),
};

const filterOutQuestion = question => q => q.id !== question.id;

const schema = {
    name: Joi.string().trim().required().messages({ 'string.empty': texts.SAISIR_LE_CHAMP }),
    questions: Joi.array().min(1).messages({ 'array.min': texts.ACTION_QUESTIONS_REQUIRED }),
    conditions: Joi.array().min(1).messages({ 'array.min': texts.ACTION_CONDITIONS_REQUIRED }),
    alertMessage: Joi.string()
        .trim()
        .required()
        .messages({ 'string.empty': texts.SAISIR_LE_CHAMP }),
};

const UpdateAction = ({ ppTunnelId, pmTunnelId, action }) => {
    const dispatch = useDispatch();
    const {
        open: confirmSubmitOpen,
        onClose: handleSubmitClose,
        onOpen: handleSaveOpen,
    } = useDialog();

    const { ppId: editingConditionId } = useSelector(customTunnelSelectors.getEditingConditionId);

    const { data: ppTunnelQuestions, isLoading: ppQuestionsLoading } = useGetQuestionsQuery({
        tunnelId: ppTunnelId,
        pagination: false,
    });
    const { data: pmTunnelQuestions, isLoading: pmQuestionsLoading } = useGetQuestionsQuery({
        tunnelId: pmTunnelId,
        pagination: false,
    });
    const { data: ppActionConditions, isLoading: ppConditionsLoading } =
        useGetActionConditionsQuery({ tunnelAction: action?.id }, { skip: !action?.id });
    const { data: pmActionConditions, isLoading: pmConditionsLoading } =
        useGetActionConditionsQuery(
            { tunnelAction: action?.matchingTunnelAction?.id },
            { skip: !action?.matchingTunnelAction?.id }
        );
    const [updateAction, { isLoading: updateLoading, isSuccess: updateSuccess }] =
        useUpdateTunnelActionMutation();

    const [selectAll, setSelectAll] = useState(false);
    const [form, setForm] = useState({
        name: '',
        questions: [],
        conditions: [],
        alertType: alertType.WARNING,
        alertMessage: '',
        wrongCharacter: false,
    });
    const [errors, setErrors] = useState({
        name: '',
        questions: '',
        conditions: '',
        alertMessage: '',
    });

    const canAddCondition = useMemo(
        () =>
            form.conditions.length !== 2 &&
            !form.conditions.find(c => c.type === conditionType.EQUAL),
        [form.conditions]
    );

    const handleSelectAllQuestions = useCallback(
        e => {
            const { checked } = e.target;
            setSelectAll(checked);
            setErrors(prevState => ({ ...prevState, questions: '' }));
            setForm(prevState => ({
                ...prevState,
                questions: checked ? [...ppTunnelQuestions] : [],
            }));
        },
        [ppTunnelQuestions]
    );
    const handleSelectQuestion = useCallback(
        question => e => {
            const { checked } = e.target;
            if (checked) {
                setForm(prevState => ({
                    ...prevState,
                    questions: [...prevState.questions, question],
                }));
            } else {
                setForm(prevState => ({
                    ...prevState,
                    questions: prevState.questions.filter(filterOutQuestion(question)),
                }));
            }
        },
        []
    );
    const handleChange = useCallback(
        key => e => {
            setForm(prevState => ({ ...prevState, [key]: e.target.value }));
        },
        []
    );
    const handleCheckbox = useCallback(e => {
        setForm(prevState => ({ ...prevState, wrongCharacter: e.target.checked }));
    }, []);
    const handleAddCondition = useCallback(() => {
        setForm(prevState => ({
            ...prevState,
            conditions: [
                ...prevState.conditions,
                { type: conditionType.LESS_THAN, limitValue: '' },
            ],
        }));
    }, []);
    const handleCleanupConditions = useCallback(() => {
        setForm(prevState => ({
            ...prevState,
            conditions: prevState.conditions.filter(c => c.id || c.limitValue),
        }));
    }, []);
    const handleCancel = useCallback(() => {
        dispatch(editingActionIdSet(null));
        dispatch(editingConditionIdSet(null));
    }, [dispatch]);

    const handleSubmit = useCallback(() => {
        const payload = customTunnelSchemas.updateAction({
            ppId: action.id,
            pmId: action.matchingTunnelAction?.id,
            ppQuestions: ppTunnelQuestions,
            pmQuestions: pmTunnelQuestions,
            action: form,
        });
        updateAction(payload);
    }, [form, updateAction, action, ppTunnelQuestions, pmTunnelQuestions]);

    const handleConfirmSubmit = useCallback(() => {
        const { newErrors, valid } = validateSchema(schema, form);
        if (valid) {
            setErrors({ name: '' });
            if (editingConditionId) {
                handleSaveOpen();
            } else {
                handleSubmit();
            }
        } else {
            setErrors(newErrors);
        }
    }, [editingConditionId, form, handleSubmit, handleSaveOpen]);

    const handleRemoveCondition = useCallback(
        index => () => {
            setForm(prevState => {
                let newConditions = [...prevState.conditions];
                newConditions.splice(index, 1);
                return { ...prevState, conditions: newConditions };
            });
        },
        []
    );

    useEffect(() => {
        if (updateSuccess) {
            // clear store data
            dispatch(editingActionIdSet(null));
        }
    }, [updateSuccess, dispatch]);

    useEffect(() => {
        if (
            action?.id &&
            ppTunnelQuestions?.length &&
            (ppActionConditions?.length >= 0 || !action?.id) &&
            (pmActionConditions?.length >= 0 || !action?.matchingTunnelAction?.id)
        ) {
            // use saved data for editing - only run once when all data is loaded
            if (action.questions.length === ppTunnelQuestions.length) {
                setSelectAll(true);
            }
            setForm({
                name: action.name,
                questions: action.questions
                    .map(q => ppTunnelQuestions.find(ppQ => q.id === ppQ.id))
                    .filter(Boolean), // filter out undefined values
                conditions: ppActionConditions?.map(c => ({
                    ...c,
                    pmId: pmActionConditions?.find(pmC => pmC.type === c.type)?.id, // match by type as it is unique per action
                })),
                alertType: action.alertType,
                alertMessage: action.alertMessage,
                wrongCharacter: action.wrongCharacter,
            });
        }
    }, [action, ppTunnelQuestions, ppActionConditions, pmActionConditions]);

    return (
        <Box sx={theme => ({ padding: theme.spacing(2, 3) })}>
            <Loader loading={ppConditionsLoading || pmConditionsLoading}>
                <CustomTextField
                    label={texts.ACTION_NAME}
                    value={form.name}
                    onChange={handleChange('name')}
                />
                <CustomFormLabel>{texts.QUESTIONS}</CustomFormLabel>
                <CheckboxLabel
                    id="select-all"
                    label={texts.SELECT_ALL}
                    checked={selectAll}
                    sx={{ alignItems: 'center' }}
                    onChange={handleSelectAllQuestions}
                />
                <Loader loading={ppQuestionsLoading || pmQuestionsLoading}>
                    {ppTunnelQuestions?.map(q => (
                        <CheckboxLabel
                            key={q.id}
                            label={q.title}
                            checked={!!form.questions.find(crt => crt.id === q.id)}
                            sx={{ width: '100%' }}
                            onChange={handleSelectQuestion(q)}
                        />
                    ))}
                </Loader>
                <ErrorMessage message={errors.questions} />
                <ScoringLimits questions={form.questions} />
                <Box textAlign="center" mb={2}>
                    <ActionButton
                        disabled={!canAddCondition}
                        variant="contained"
                        onClick={handleAddCondition}
                    >
                        {texts.ADD_CONDITION}
                    </ActionButton>
                </Box>
                {form.conditions.map((c, idx) => (
                    <ConditionItem
                        key={`${c.type}-${c.limitValue}`}
                        pmConditions={pmActionConditions}
                        condition={c}
                        index={idx}
                        onRemove={handleRemoveCondition(idx)}
                        formCleanup={handleCleanupConditions}
                        editingAction={form}
                    />
                ))}
                <ErrorMessage message={errors.conditions} />
                <CustomSelect
                    label={texts.ALERTS.ALERTS}
                    value={form.alertType}
                    onChange={handleChange('alertType')}
                >
                    {Object.values(alertType).map(val => (
                        <MenuItem key={val} value={val}>
                            {alertLabels[val]}
                        </MenuItem>
                    ))}
                </CustomSelect>
                <CustomTextField
                    label={texts.ALERTS.ALERT_TEXT}
                    value={form.alertMessage}
                    onChange={handleChange('alertMessage')}
                />
                <CheckboxLabel
                    label={texts.ALERTS.WRONG_CHARACTER}
                    checked={form.wrongCharacter}
                    onChange={handleCheckbox}
                />
            </Loader>
            <Box display="flex" justifyContent="center" flexDirection="column" alignItems="center">
                <ConfirmDialog
                    open={confirmSubmitOpen}
                    handleConfirm={handleSubmit}
                    handleClose={handleSubmitClose}
                    title={texts.CHANGES_NOT_CONFIRMED}
                    confirmText={texts.CONFIRMER}
                    cancelText={texts.CANCEL}
                >
                    {texts.CONFIRM_DISCARD_CHANGES}
                </ConfirmDialog>
                <ActionButton
                    loading={updateLoading}
                    variant="contained"
                    onClick={handleConfirmSubmit}
                >
                    {texts.SAVE}
                </ActionButton>
                <CancelButton disabled={updateLoading} onClick={handleCancel}>
                    {texts.CANCEL}
                </CancelButton>
            </Box>
        </Box>
    );
};

UpdateAction.propTypes = {
    action: PropTypes.object,
    ppTunnelId: PropTypes.number,
    pmTunnelId: PropTypes.number,
};

export default UpdateAction;
